import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedLines } from "@/components/site/Motion";
import { CONTACT } from "@/data/site";

const Badge = () => (
  <Link to="/contacto" data-testid="hero-badge-link" className="group relative hidden h-36 w-36 place-items-center sm:grid" aria-label="Pedir orçamento">
    <svg viewBox="0 0 200 200" className="absolute inset-0 animate-spin-slow">
      <defs>
        <path id="circ" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" />
      </defs>
      <text className="fill-white font-mono text-[15px] uppercase tracking-[0.32em]">
        <textPath href="#circ">Pedir orçamento · Dog'z · Braga ·</textPath>
      </text>
    </svg>
    <span className="relative h-12 w-12 rounded-full bg-[#C8D484] transition-transform duration-500 group-hover:scale-125 sm:h-16 sm:w-16">
      <svg viewBox="0 0 40 40" className="h-full w-full">
        <path d="M6 12c7 4 8 13 3 22M34 12c-7 4-8 13-3 22" fill="none" stroke="#08090B" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </span>
  </Link>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 80, damping: 20 });
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 80, damping: 20 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden bg-[#08090B] pt-20" style={{ perspective: 1400 }}>
      <motion.div style={{ y, rotateX: rotX, rotateY: rotY }} className="absolute inset-y-0 right-0 w-full lg:w-[62%]">
        <motion.div
          initial={{ clipPath: "inset(12% 8% 12% 30%)", scale: 1.2 }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)", scale: 1 }}
          transition={{ duration: 1.8, ease: [0.76, 0, 0.24, 1] }}
          className="h-full w-full"
        >
          <img src="/img/hero.jpg" alt="Pastor Alemão" className="h-full w-full object-cover object-[60%_30%]" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#08090B] via-[#08090B]/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#08090B] via-transparent to-[#08090B]/30" />
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10 mx-auto flex min-h-[calc(100svh-5rem)] max-w-[1440px] flex-col justify-end px-5 pb-14 sm:px-8 lg:px-12 lg:pb-20">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }} className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-[#C8D484]">
          DOG´Z Training & Hotel — Palmeira, Braga
        </motion.p>
        <MaskedLines
          testId="hero-title"
          lines={["Hotel.", "Creche.", "Treino.", "Família"]}
          className="font-display text-[21vw] font-semibold uppercase leading-[0.84] tracking-[-0.01em] sm:text-[13vw] lg:text-[8.4vw]"
          lineClassName={(i) => (i === 3 ? "font-serif text-[#C8D484] italic normal-case font-normal tracking-normal" : "")}
        />
        <div className="mt-10 flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.8 }} className="max-w-md text-base text-white/80 sm:text-lg">
            O lugar onde o seu cão aprende, brinca, descansa e é tratado como parte da família — com rigor de quem vive para os cães.
          </motion.p>
          <motion.div initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.4, duration: 0.8 }} className="flex items-center gap-6">
            <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-button" className="rounded-full border border-white/30 px-6 py-3 text-sm font-semibold backdrop-blur-md transition-colors hover:border-[#C8D484] hover:text-[#C8D484]">
              Falar no WhatsApp
            </a>
            <Badge />
          </motion.div>
        </div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }} className="mt-10 hidden items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-white/60 lg:flex">
          <ArrowDown className="h-4 w-4 animate-bounce" /> Scroll
        </motion.div>
      </motion.div>
    </section>
  );
};
